import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const DeleteConfirmModal = ({ isOpen, onClose, data, onConfirm }) => {
  const [isDeleting, setIsDeleting] = useState(false);

  const handleConfirm = async () => {
    setIsDeleting(true);
    try {
      await onConfirm(data);
      onClose();
    } catch (error) {
      console.error('Error deleting item:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  if (!isOpen || !data) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-1001 p-4">
      <div className="bg-card border border-border rounded-lg w-full max-w-md">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-error/10 rounded-full flex items-center justify-center">
              <Icon name="AlertTriangle" size={20} className="text-error" />
            </div>
            <h2 className="text-lg font-semibold text-foreground">Confirm Deletion</h2>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            iconName="X"
            iconSize={20}
          />
        </div>

        {/* Modal Content */}
        <div className="p-6">
          <div className="space-y-4">
            <p className="text-foreground">
              Are you sure you want to delete <strong>{data.name}</strong>?
            </p>
            <p className="text-sm text-muted-foreground">
              This action cannot be undone. All associated data and records will be permanently removed from the system.
            </p>
            
            {/* Item Details */}
            <div className="bg-muted/50 rounded-lg p-4">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                  <span className="text-sm font-medium text-primary">
                    {data.name?.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div>
                  <p className="font-medium text-foreground">{data.name}</p>
                  <p className="text-xs text-muted-foreground">{data.code}</p>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-muted-foreground">Contact:</span>
                  <span className="text-foreground ml-1">{data.contact?.phone}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Status:</span>
                  <span className={`ml-1 px-1.5 py-0.5 rounded text-xs ${
                    data.status === 'active' ? 'bg-success/10 text-success' :
                    data.status === 'inactive'? 'bg-error/10 text-error' : 'bg-warning/10 text-warning'
                  }`}>
                    {data.status?.charAt(0).toUpperCase() + data.status?.slice(1)}
                  </span>
                </div>
              </div>
            </div>

            {/* Warning Message */}
            <div className="flex items-start space-x-2 p-3 bg-warning/10 border border-warning/20 rounded-lg">
              <Icon name="AlertCircle" size={16} className="text-warning mt-0.5 flex-shrink-0" />
              <div className="text-sm">
                <p className="font-medium text-warning">Warning</p>
                <p className="text-warning/80 mt-1">
                  Deleting this record may affect related transactions, orders, and reports. 
                  Please ensure no active dependencies exist before proceeding.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-border">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            loading={isDeleting}
            iconName="Trash2"
            iconPosition="left"
            iconSize={16}
          >
            Delete Permanently
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DeleteConfirmModal;